import { createEffect, createMemo, createSignal, on, untrack, type Accessor } from 'solid-js';
import { AUDIO_REACTIVE_PACKAGE_ID, rememberedEffectSettings, type EffectPackageItem } from '../lib/effectPackages';
import { parseEffectStack, type EffectStack, type EffectStackLayer, type EffectStackPatch } from '../lib/effectStack';
import type { ActiveEffect } from '../lib/types';
import type { VenueContextValue } from './VenueProvider';

export interface SettingWrite { path: string; value: unknown }
const EFFECT_SETTINGS_COMMAND = 'set_effect_settings';

export interface EffectSettings {
  packageId: string;
  parameters: Record<string, unknown>;
  sync?: { enabled: boolean; musicRate: string };
  mix?: 'max' | 'add';
  layers?: EffectStackLayer[];
}

function record(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown> : undefined;
}

/** Read the same settings leaf from cloud-authored and LAN state entries. */
export function effectSettingsFrom(entry: ActiveEffect | undefined): EffectSettings | undefined {
  const value = record(entry?.settings);
  if (!value || typeof value.packageId !== 'string') return undefined;
  const settings: EffectSettings = { packageId: value.packageId, parameters: record(value.parameters) ?? {} };
  const sync = record(value.sync);
  if (sync) settings.sync = { enabled: sync.enabled === true, musicRate: String(sync.musicRate ?? 'bar') };
  if (value.mix === 'max' || value.mix === 'add') settings.mix = value.mix;
  if (Array.isArray(value.layers)) settings.layers = parseEffectStack({ mix: settings.mix, layers: value.layers }).layers;
  return settings;
}

/** A stopped card reads the room's durable room document through this one composer. */
export function rememberedSettings(roomDoc: unknown, packageId: string): EffectSettings {
  const doc = record(roomDoc);
  const stored = rememberedEffectSettings(doc?.effectParameters, packageId);
  const settings: EffectSettings = { packageId, parameters: stored?.parameters ?? {} };
  if (packageId === AUDIO_REACTIVE_PACKAGE_ID) {
    const stack = parseEffectStack(record(doc?.effectStacks)?.[packageId]);
    settings.mix = stack.mix;
    settings.layers = stack.layers;
  } else if (stored?.sync !== undefined) {
    settings.sync = { enabled: stored.sync, musicRate: stored.musicRate ?? 'bar' };
  }
  return settings;
}

/** K1 node-instance bindings are the saved settings for a bound room rig. */
export function recordEffectSettings(state: unknown, packageId: string): EffectSettings {
  const saved = record(state);
  const bindings = record(saved?.bindings) ?? {};
  const settings: EffectSettings = { packageId, parameters: { ...bindings } };
  if (packageId === AUDIO_REACTIVE_PACKAGE_ID) {
    if (bindings.mix === 'add' || bindings.mix === 'max') settings.mix = bindings.mix;
    delete settings.parameters.mix;
    settings.layers = parseEffectStack({ mix: settings.mix, layers: saved?.layers }).layers;
  }
  return settings;
}

export function settingsEqual(a: unknown, b: unknown): boolean {
  if (typeof a === 'number' && typeof b === 'number') return Math.abs(a - b) < 0.0005;
  if (Array.isArray(a) || Array.isArray(b)) {
    return Array.isArray(a) && Array.isArray(b) && a.length === b.length
      && a.every((value, index) => settingsEqual(value, b[index]));
  }
  const left = record(a);
  const right = record(b);
  if (left || right) {
    if (!left || !right || Object.keys(left).length !== Object.keys(right).length) return false;
    return Object.keys(left).every((key) => key in right && settingsEqual(left[key], right[key]));
  }
  return a === b;
}

function clone(settings: EffectSettings): EffectSettings {
  return JSON.parse(JSON.stringify(settings)) as EffectSettings;
}

function pathValue(settings: EffectSettings | undefined, path: string): unknown {
  if (!settings) return undefined;
  const parts = path.split('/');
  if (path === 'mix') return settings.mix;
  if (path === 'layers') return settings.layers?.map(({ zone, effectId }) => ({ zone, effectId })) ?? [];
  if (parts[0] === 'parameters') return settings.parameters[parts[1]];
  if (parts[0] === 'sync') return settings.sync?.[parts[1] as 'enabled' | 'musicRate'];
  if (parts[0] !== 'layers') return undefined;
  const layers = settings.layers ?? [];
  const layer = layers.find((entry) => entry.id === parts[1]);
  if (parts.length === 2) return layer ?? null;
  if (!layer) return undefined;
  if (parts[2] === 'index') return layer.zone === 'reactive'
    ? layers.filter((entry) => entry.zone === 'reactive').findIndex((entry) => entry.id === layer.id)
    : undefined;
  if (parts[2] === 'effectId') return layer.effectId;
  if (parts[2] === 'parameters') return layer.parameters[parts[3]];
  if (parts[2] === 'sync') return layer.sync[parts[3] as 'enabled' | 'musicRate'];
  if (parts[2] === 'driver') return layer.driver?.[parts[3] as keyof NonNullable<typeof layer.driver>];
  return undefined;
}

function reportedMatches(settings: EffectSettings | undefined, write: SettingWrite): boolean {
  const parts = write.path.split('/');
  if (parts[0] === 'layers' && parts.length === 2 && write.value !== null) {
    const reported = pathValue(settings, write.path) as EffectStackLayer | null | undefined;
    const wanted = record(write.value);
    return Boolean(reported && wanted && reported.effectId === wanted.effectId && reported.zone === wanted.zone);
  }
  return settingsEqual(pathValue(settings, write.path), write.value);
}

/**
 * Whether a new layer of this package starts locked to the music: the package's `sync.defaultLocked`
 * (absent: locked), the controller's first-use rule. Undefined when the package is not in the catalog.
 */
export type FirstUseLocked = (effectId: string) => boolean | undefined;

function overlay(settings: EffectSettings, write: SettingWrite, firstUseLocked?: FirstUseLocked): void {
  const parts = write.path.split('/');
  if (write.path === 'mix') { settings.mix = write.value as 'max' | 'add'; return; }
  if (write.path === 'layers') {
    const entries = Array.isArray(write.value) ? write.value : [];
    settings.layers = parseEffectStack({ mix: settings.mix, layers: entries.map((entry, index) => ({
      ...record(entry), id: `preview-${index}`,
    })) }).layers;
    return;
  }
  if (parts[0] === 'parameters') { settings.parameters[parts[1]] = write.value; return; }
  if (parts[0] === 'sync') {
    settings.sync ??= { enabled: false, musicRate: 'bar' };
    (settings.sync as unknown as Record<string, unknown>)[parts[1]] = write.value;
    return;
  }
  if (parts[0] !== 'layers') return;
  settings.layers ??= [];
  const index = settings.layers.findIndex((layer) => layer.id === parts[1]);
  if (parts.length === 2) {
    if (write.value === null) { if (index >= 0) settings.layers.splice(index, 1); return; }
    if (index < 0) {
      const value = record(write.value);
      if (!value) return;
      const layer = {
        id: parts[1], effectId: String(value.effectId), zone: value.zone,
        parameters: record(value.parameters) ?? {},
        // An add carries no sync: the room applies the package's first-use rule, and so does this placeholder.
        sync: record(value.sync) ?? { enabled: firstUseLocked?.(String(value.effectId)) ?? true, musicRate: 'bar' },
        ...(value.zone === 'reactive' ? { driver: record(value.driver) ?? { source: 'hit', threshold: 0.25, amount: 1, attack: 0.01, release: 0.25, target: 'output' } } : {}),
      } as EffectStackLayer;
      if (layer.zone === 'baseline') settings.layers.unshift(layer);
      else settings.layers.push(layer);
    }
    return;
  }
  if (index < 0) return;
  const layer = settings.layers[index];
  if (parts[2] === 'index') {
    if (layer.zone !== 'reactive') return;
    const reactive = settings.layers.filter((entry) => entry.zone === 'reactive' && entry.id !== layer.id);
    reactive.splice(Number(write.value), 0, layer);
    settings.layers = [...settings.layers.filter((entry) => entry.zone === 'baseline'), ...reactive];
  } else if (parts[2] === 'effectId') layer.effectId = String(write.value);
  else if (parts[2] === 'parameters') layer.parameters[parts[3]] = write.value as never;
  else if (parts[2] === 'sync') (layer.sync as unknown as Record<string, unknown>)[parts[3]] = write.value;
  else if (parts[2] === 'driver') {
    layer.driver ??= { source: 'hit', threshold: 0.25, amount: 1, attack: 0.01, release: 0.25, target: 'output' };
    (layer.driver as unknown as Record<string, unknown>)[parts[3]] = write.value;
  }
}

export interface SettingHold {
  shown: Accessor<EffectSettings>;
  committing: (path: string) => boolean;
  holding: (path: string) => boolean;
  stage: (writes: readonly SettingWrite[], gestureKey: string, final: boolean) => number;
  applied: (writes: readonly SettingWrite[], run: number) => void;
  rejected: (writes: readonly SettingWrite[], run: number) => void;
  unanswered: (writes: readonly SettingWrite[], run: number) => void;
  clearReplacedLayers: () => void;
}

/** The address is the hold key, the command address and the feed address. */
export function createSettingHold(
  reported: Accessor<EffectSettings>, instance: Accessor<string>, firstUseLocked?: FirstUseLocked,
): SettingHold {
  const [pending, setPending] = createSignal<Map<string, SettingWrite>>(new Map());
  const [counts, setCounts] = createSignal<Map<string, number>>(new Map());
  const runs = new Map<number, string[]>();
  const open = new Map<string, number>();
  const before = new Map<string, unknown>();
  const acknowledged = new Set<string>();
  let sequence = 0;
  let priorInstance = '';
  createEffect(on([reported, instance], ([current, nextInstance]) => {
    const changed = priorInstance !== '' && priorInstance !== nextInstance;
    priorInstance = nextInstance;
    setPending((existing) => {
      const next = new Map(existing);
      for (const [path, write] of existing) {
        const value = pathValue(current, path);
        if (reportedMatches(current, write) || (changed && !untrack(counts).has(path))
          || (acknowledged.has(path) && !settingsEqual(value, before.get(path)))) {
          next.delete(path);
          before.delete(path);
          acknowledged.delete(path);
        }
      }
      return next.size === existing.size ? existing : next;
    });
  }));
  const shown = createMemo(() => {
    const value = clone(reported());
    for (const write of pending().values()) overlay(value, write, firstUseLocked);
    return value;
  });
  const committing = (path: string): boolean => (counts().get(path) ?? 0) > 0;
  const holding = (path: string): boolean => pending().has(path);
  const stage = (writes: readonly SettingWrite[], gestureKey: string, final: boolean): number => {
    setPending((existing) => {
      const next = new Map(existing);
      for (const write of writes) {
        if (!next.has(write.path)) before.set(write.path, pathValue(untrack(reported), write.path));
        acknowledged.delete(write.path);
        next.set(write.path, write);
      }
      return next;
    });
    let run = open.get(gestureKey);
    if (run === undefined) {
      run = ++sequence;
      open.set(gestureKey, run);
      const paths = [...new Set(writes.map((write) => write.path))];
      runs.set(run, paths);
      setCounts((existing) => {
        const next = new Map(existing);
        for (const path of paths) next.set(path, (next.get(path) ?? 0) + 1);
        return next;
      });
    }
    if (final) open.delete(gestureKey);
    return run;
  };
  const release = (writes: readonly SettingWrite[], run: number, outcome: 'applied' | 'rejected' | 'unanswered'): void => {
    const paths = runs.get(run);
    if (!paths) return;
    runs.delete(run);
    setCounts((existing) => {
      const next = new Map(existing);
      for (const path of paths) {
        const count = (next.get(path) ?? 0) - 1;
        if (count > 0) next.set(path, count);
        else next.delete(path);
      }
      return next;
    });
    if (outcome === 'rejected') setPending((existing) => {
      const next = new Map(existing);
      for (const path of paths) {
        const sent = writes.find((write) => write.path === path);
        if (!counts().has(path) && sent && settingsEqual(next.get(path)?.value, sent.value)) {
          next.delete(path);
          before.delete(path);
          acknowledged.delete(path);
        }
      }
      return next;
    });
    if (outcome === 'applied') for (const path of paths) {
      const sent = writes.find((write) => write.path === path);
      if (!counts().has(path) && sent && settingsEqual(untrack(pending).get(path)?.value, sent.value)) {
        acknowledged.add(path);
      }
    }
  };
  const clearReplacedLayers = (): void => {
    for (const path of before.keys()) if (path.startsWith('layers/')) before.delete(path);
    for (const path of acknowledged) if (path.startsWith('layers/')) acknowledged.delete(path);
    setPending((existing) => new Map([...existing].filter(([path]) => !path.startsWith('layers/'))));
    setCounts((existing) => new Map([...existing].filter(([path]) => !path.startsWith('layers/'))));
    for (const [key, paths] of runs) if (paths.some((path) => path.startsWith('layers/'))) runs.delete(key);
    for (const [key, run] of open) if (!runs.has(run)) open.delete(key);
  };
  return {
    shown, committing, holding, stage,
    applied: (writes, run) => release(writes, run, 'applied'),
    rejected: (writes, run) => release(writes, run, 'rejected'),
    unanswered: (writes, run) => release(writes, run, 'unanswered'),
    clearReplacedLayers,
  };
}

/**
 * The Audio reactive card's settings as the room reports them, and its hold.
 *
 * Two cards write this card's settings: Audio reactive itself, and Auto VJ's Play, which puts a look on
 * the room through the same stack replace (B72). Each builds its hold here, from the one reading of the
 * running entry or the remembered room document, so the two can never read the card differently.
 */
export function createAudioReactiveHold(venue: VenueContextValue, catalog: Accessor<readonly EffectPackageItem[]>): {
  effectId: Accessor<string>;
  reported: Accessor<EffectSettings>;
  hold: SettingHold;
} {
  const live = createMemo(() => venue.controllerState()?.effects?.find((entry) => entry.kind === 'audio_reactive'));
  const effectId = createMemo(() => String(live()?.id ?? live()?.effectId ?? ''));
  const reported = createMemo(() => {
    const saved = venue.microRecordWrites() ? venue.effectRecord?.(AUDIO_REACTIVE_PACKAGE_ID) : undefined;
    return saved ? recordEffectSettings(saved.record.state, AUDIO_REACTIVE_PACKAGE_ID)
      : effectSettingsFrom(live()) ?? rememberedSettings(venue.roomDoc(), AUDIO_REACTIVE_PACKAGE_ID);
  });
  const firstUseLocked: FirstUseLocked = (packageId) => {
    const item = catalog().find((entry) => entry.packageId === packageId);
    return item ? item.sync.defaultLocked ?? true : undefined;
  };
  return { effectId, reported, hold: createSettingHold(reported, () => effectId() || 'stopped', firstUseLocked) };
}

export function settingGestureKey(packageId: string, firstAddress: string): string {
  return `effect-settings:${packageId}:${firstAddress}`;
}

/** All card gestures use this sender, including every Audio reactive layer operation. */
export function sendSettingGesture(
  venue: VenueContextValue, packageId: string, writes: SettingWrite[], final: boolean,
  hold: SettingHold, onRejected?: (error: unknown) => void, onUnanswered?: (error: unknown) => void,
): void {
  if (!writes.length) return;
  if (!venue.controlAvailable()) {
    onRejected?.(new Error('Change not applied.'));
    return;
  }
  const key = settingGestureKey(packageId, writes[0].path);
  const run = hold.stage(writes, key, final);
  if (venue.microRecordWrites()) {
    if (final) void venue.writeEffectRecord({ packageId, set: writes }).then(() => {
      hold.applied(writes, run);
    }).catch((error) => {
      hold.rejected(writes, run);
      onRejected?.(error);
    });
    return;
  }
  venue.controllerGesture(EFFECT_SETTINGS_COMMAND, { packageId, set: writes }, key, final,
    final ? () => hold.applied(writes, run) : undefined,
    final ? (error) => { hold.rejected(writes, run); onRejected?.(error); } : undefined,
    final ? (error) => { hold.unanswered(writes, run); onUnanswered?.(error); } : undefined);
}

export async function sendSettingAwaited(
  call: (name: string, params: Record<string, unknown>) => Promise<unknown>,
  packageId: string, writes: SettingWrite[], hold: SettingHold | undefined, venue: VenueContextValue,
): Promise<unknown> {
  if (!venue.controlAvailable()) throw new Error('Change not applied.');
  const run = hold?.stage(writes, settingGestureKey(packageId, writes[0].path), true);
  try {
    const result = venue.microRecordWrites()
      ? await venue.writeEffectRecord({ packageId, set: writes })
      : await call(EFFECT_SETTINGS_COMMAND, { packageId, set: writes });
    if (run !== undefined) hold?.applied(writes, run);
    return result;
  } catch (error) {
    if (run !== undefined) hold?.rejected(writes, run);
    throw error;
  }
}

export function newLayerId(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return `lyr_${[...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('')}`;
}

/** Translate the editor's transport-agnostic patch to incremental command addresses. */
export function stackPatchWrites(patch: EffectStackPatch, current: EffectStack): SettingWrite[] {
  if ('mix' in patch) return [{ path: 'mix', value: patch.mix }];
  if ('add' in patch) return [{ path: `layers/${newLayerId()}`, value: { effectId: patch.add.effectId, zone: 'reactive' } }];
  if ('remove' in patch) return [{ path: `layers/${patch.remove}`, value: null }];
  if ('baseline' in patch) {
    const existing = current.layers.find((layer) => layer.zone === 'baseline');
    if (patch.baseline === null) return existing ? [{ path: `layers/${existing.id}`, value: null }] : [];
    return existing
      ? [{ path: `layers/${existing.id}/effectId`, value: patch.baseline.effectId }]
      : [{ path: `layers/${newLayerId()}`, value: { effectId: patch.baseline.effectId, zone: 'baseline' } }];
  }
  if ('order' in patch) {
    const before = current.layers.filter((layer) => layer.zone === 'reactive').map((layer) => layer.id);
    const moved = patch.movedId ?? patch.order.find((id, index) => before[index] !== id);
    return moved ? [{ path: `layers/${moved}/index`, value: patch.order.indexOf(moved) }] : [];
  }
  if ('replace' in patch) return [
    { path: 'mix', value: patch.replace.mix },
    { path: 'layers', value: patch.replace.layers.map(({ id: _id, ...layer }) => layer) },
  ];
  const layer = patch.layer;
  const root = `layers/${layer.id}`;
  return [
    ...(layer.effectId !== undefined ? [{ path: `${root}/effectId`, value: layer.effectId }] : []),
    ...Object.entries(layer.parameters ?? {}).map(([key, value]) => ({ path: `${root}/parameters/${key}`, value })),
    ...Object.entries(layer.sync ?? {}).map(([key, value]) => ({ path: `${root}/sync/${key}`, value })),
    ...Object.entries(layer.driver ?? {}).map(([key, value]) => ({ path: `${root}/driver/${key}`, value })),
  ];
}
